import { useMemo, useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { nanoid } from '@reduxjs/toolkit';
import { useSelector } from "react-redux";
import { selectCurrentPage } from "context/pages/pageSlice";

import { useAddNewOrderMutation } from "context/order/orderApiSlice";
import { useGetProductQuery } from "context/product/productApiSlice";
import { useGetDistributersQuery } from "context/distributer/distributerApiSlice";
import { useLazyGetUserConfigQuery } from "context/config/configApiSlice";

import {  Grid, Card, Container, Divider, ListItem, List, Badge } from "@mui/material";

import MDTypography from "dashboard/components/MDTypography";
import MDButton from "dashboard/components/MDButton";
import MDBox from "dashboard/components/MDBox";
import MDAlert from "dashboard/components/MDAlert";
import MDAvatar from "dashboard/components/MDAvatar";
import Breadcrumbs from "dashboard/examples/Breadcrumbs";
import MDBadge from "dashboard/components/MDBadge";

import useCart from "hooks/useCart";
import CheckoutForm from "components/Author/Forms"
import Navigation  from "components/Home/components/navigation";
import InfoCard from "components/Home/distributers/components/InfoCard";
// Images
import bgImage from "assets/images/bg-profile.jpeg";
import carImage from "assets/images/small-logos/car.webp";
import orderImage from "assets/images/small-logos/order.webp";
import paymentImage from "assets/images/small-logos/payment.webp";
import returnImage from "assets/images/small-logos/return.webp";
import CartList from "components/Home/products/components/CartList";
import useAlert from "hooks/useAlert";
import Footer from "components/layouts/SimpleFooter";

const ShoppinCartView = () => {
  const pageContent = useSelector(selectCurrentPage);
	const [store, setStore] = useState(null);
  const [distributor, setDistributor] = useState(null);
  const [connectedUser, setConnectedUser] = useState(null);
  let productList=[]; 
	const [getConfig, {isLoading : fetchLoading}] =  useLazyGetUserConfigQuery() ;
  const {phase}       = useParams();
  const {pathname}    = useLocation();
  const {showAlerts} = useAlert();
  const { cart, addItem, removeItem, cartTotal, cartNumberTotal, emptyCart } = useCart();

  const [createOrder,{isLoading : isCommandeLoading, isSuccess : isCommandeSuccess}] = useAddNewOrderMutation();
  const loadConfig = async (section) =>{    
    try{
      const configuration = await getConfig({configType : section , active : 1}).unwrap();
      setStore(configuration) ;
    }catch(error){
      showAlerts({code:"error", msg : error.error, title: "Préférences " ,type : "toast"});
    }
  };

  useEffect(() => {
    loadConfig("fiscalité");
  }, []);

  const attribute = useMemo(() =>({
      cart : JSON.stringify(cart.items.map(item=>item.id))
  }) ,[cart.items]);

  const distributersQuery = useMemo(() => {   
      return({
      filters: JSON.stringify({ productIds: attribute.cart, ...connectedUser }) ,
     })},
    [connectedUser, attribute.cart]
  );

  const route = pathname.split("/").slice(1);
  const {
    data: products = [],
    isLoading,
    isSuccess,
    isFetching,
    isError,
    error,
  } = useGetProductQuery(attribute, {
      // pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
      skip : cart.items?.length === 0 
  });

  const { data: { entities: distributers = [] } = {}, isSuccess : distSuccess, isLoading: distLoading } = useGetDistributersQuery(
    distributersQuery,
    {
      refetchOnMountOrArgChange: true,
      skip: attribute.cart?.length === 0 || connectedUser === null
    }
  );
  

  if (isSuccess) {
    const { entities } = products;
    productList = Object.values(entities);
  }

  if(isError) {
    return <MDAlert color="error" >messsage : {error.data.error}</MDAlert>
  }
  const handleDistributorClick = id =>{
    setDistributor(id)
  }
  /*eslint-disable*/
  const newOrderFunction = async (update) => {
    if(cart.items.length === 0 ){
      return showAlerts({code:"warning", msg : "Votre panier est encore vide, choisissez quelques articles à acheter", title: "Empty basket" ,type : "toast"});
    }else if(!distributor){
      return showAlerts({code:"info", msg : "Veuillez choisir un distributeur avant de passer votre commande.", title: "Distributeur inconnu" ,type : "toast"});
    }
    const TOTAL = cartNumberTotal(productList);
    const TAX = store?.configData.taxesPrice || 0.19;
    const SHIPPINGPRICE = store?.configData.shippingPrice || 400;
    const TAXPRICE = (TOTAL + SHIPPINGPRICE) * TAX;
    const dateObj  = new Date();
    const month    = dateObj.getUTCMonth() + 1; //  months from 1-12
    const year     = dateObj.getUTCFullYear();
    const order = {
      user_id : update.user_id,
      distributor_id: distributor,
      code : `${nanoid().slice(0,4)}-${month}-${year}`, 
      orderItems: cart.items.map(item=>{
          item.product = item.id;
          return item;
      }),
      shippingAddress: {
        photo : update.selectedImage,
        displayName: update.displayName,
        email : update.email,
        phone : update.phone,
        city: update.city,
        apartement : update.apartement,
        location : update.location
      },
      paymentMethod: update.payement,
      itemsPrice: Number(TOTAL),
      shippingPrice: SHIPPINGPRICE,
      taxesPrice:  Number(TAXPRICE),
      totalPrice: TOTAL + TAXPRICE
    }
    try{
      const userData = await createOrder(order).unwrap();
      showAlerts({code:"success", msg : userData.message, title: "New order" ,type : "toast"});
      emptyCart()
      } catch(err) {
          showAlerts({code:"error", msg : err.data.error, title: "New order" ,type : "toast"});
      }
  }

  return (
    <>
        {pageContent.Navigation?.navBar && <Navigation data={{ navBar : pageContent.Navigation?.navBar, logo : pageContent.Navigation.logo?.selectedImage }}  />}
        <MDBox
            minHeight={{xs:"20vh" , lg: "55vh"}}
            width="100%"
            sx={{
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
                `${linearGradient(
                rgba(gradients.dark.main, 0.6),
                rgba(gradients.dark.state, 0.6)
                )}, url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "grid",
            placeItems: "center",
            mt:20
            }}
            >
        </MDBox>
         
        <Card
            sx={{
            px: { xs: 1, lg: 2 },
            mx: { xs: .5, lg: 3 },
            py:2,
            mt: -8,
            mb: 4,
            boxShadow: ({ boxShadows: { xxl } }) => xxl,
            backgroundColor: ({ palette: { background }, functions: { rgba } }) => rgba(background.header, 0.9),
            backdropFilter: "saturate(200%) blur(30px)",
            }}
            >
            
            <Container sx={{my:4, px:{xs:.5, lg:2}}} maxWidth="sm"  >
                <MDBox width={{ xs: "100%", lg: "50%", }} mb={3}>
                    <Breadcrumbs icon="home" title={route[route.length - 1]} route={route}  /> {/*light*/}
                </MDBox>
              <Grid container spacing={{sx:1,lg:4}} justifyContent="center" mx="auto">
                <Grid item xs={12} md={6} lg={7.5}>
                    {phase === 'UserInfos' ? 
                        <><CheckoutForm
                         phase={phase}
                         setConnectedUser={setConnectedUser}
                         action={newOrderFunction}
                         isCommandeLoading={isCommandeLoading}
                         isCommandeSuccess={isCommandeSuccess}
                         />
                         <Divider />
                          <MDBox>
                          <MDTypography variant="h4" sx={{ color:({palette})=>palette.text.main }}>Distributeurs qui répondent à votre besoin <MDBadge badgeContent={Object.values(distributers).length} variant="contained" color="secondary" /></MDTypography>
                          <Divider sx={{my:1}} />
                          {distSuccess && Object.values(distributers)?.map( distributer => 
                            <InfoCard 
                              id={distributer.id}
                              color="secondary" 
                              key={distributer.id} 
                              image={`${process.env.REACT_APP_PUBLIC_URL}/img/distributer/${distributer.photoURL}`}
                              title={distributer.companyName === "" ? distributer.displayName : distributer.companyName}
                              telephone={distributer.telephone}
                              mobile={distributer.mobile}
                              email={distributer.email}
                              website={distributer.website}
                              address={distributer.location.street}
                              province={distributer.location.state.province}
                              handleDistributorClick={()=>handleDistributorClick(distributer.id)}
                              location={{lat : distributer.location.coordinates.lat, lng : distributer.location.coordinates.lng}}
                              distributorId={distributor}
                              sendForm
                              info={{distance : distributer.distance.toFixed(2), matching_products: distributer.matching_products}}
                            />)}
                            {(!distributers || Object.values(distributers).length === 0  && !distLoading) && <MDAlert color="info" sx={{my:3}} size="sm">aucun distributeur ne peut répondre a votre commande autre que granitex </MDAlert>} 
                            </MDBox>
                        </>
                    : (
                        <CartList 
                        cart={cart} 
                        addItem={addItem} 
                        removeItem={removeItem} 
                        isLoading={isLoading || isFetching}
                        productList={productList}
                        link={Link}
                        /> 
                    )}

                </Grid> 
                <Grid item xs={12} md={6} lg={4.5} sx={{ display: "flex" }}>
                  <Divider orientation="vertical" sx={{ ml: -2, mr: 3, display:{xs:"none", lg:"block"} }} />     
                  <MDBox sx={{width:"100%", mt:1}}>
                    <Card sx={{backgroundColor:({palette})=>palette.background.card, p:2, height:"100%"}}>
                        <MDTypography variant="h4" sx={{ color:({palette})=>palette.text.main }}>Cart Totals <MDBadge badgeContent={cart.quantity} variant="contained" color="secondary" container circular /></MDTypography>
                        <Divider/>
                        <List>
                        {productList.map( product => (
                        <ListItem sx={{justifyContent:"space-between" , my:1}} alignItems="center" key={`totlal-${product.id}`}>
                            {phase === 'UserInfos' ? 
                            (<MDBox sx={{display:"flex"}} alignItems="center">
                                <Badge badgeContent={cart.items?.find(item => item.id === product.id)?.quantity} color="primary">
                                    <MDAvatar variant="rounded" shadow="sm" imgType="product" src={`${process.env.PUBLIC_URL}/assets/images/${product.selectedImage}`} alt={product.productName} size="lg"  />
                                </Badge>
                                <MDTypography variant="body2"  sx={{ fontWeight:500, ml:1.5}}>{product.productName}</MDTypography>
                            </MDBox>
                            ):(
                            <MDTypography variant="body2"  sx={{display:"flex", fontWeight:500}}>{`${product.productName} x${cart.items?.find(item => item.id === product.id)?.quantity}`}</MDTypography>
                            )}
                            <MDTypography variant="body2"  sx={{display:"flex"}}>{cartTotal([{id:product.id, price : product.price}])}</MDTypography>
                        </ListItem>
                        ))}
                        </List>
                        <Divider/>
                        <MDBox display="flex" justifyContent="flex-end" px={.5}>
                            <MDTypography  color="dark" variant="subtitle1">Total : <MDTypography  color="primary" variant="body">{cartTotal(productList)}</MDTypography></MDTypography>
                        </MDBox>
                        <Divider/>
                        <MDTypography variant="caption">Les taxes et le montant de l'expédition seront inclus plus tard</MDTypography>
                        {phase !== 'UserInfos' && <MDButton component={Link} to='/Checkout/ShoppingCart/UserInfos' disableElevation size="medium"  variant="outlined" sx={{mt:1}} color="secondary">
													poursuivre la commande
                        </MDButton>}
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </Container>

            <Container sx={{my:4}}>
                <Grid container spacing={{sx:1,lg:4}} justifyContent="space-arround" alignItems="baseline" mx="auto">
                    <Grid item xs={3} textAlign="center">
                        <MDBox component="img" alt="carImage" src={carImage}/>
                        <MDTypography sx={{color:({palette})=>palette.grey[600], display:{xs:"none", lg:"block"}}}>58 provinces Shipping</MDTypography>
                    </Grid>
                    <Grid item xs={3} textAlign="center">
                        <MDBox component="img" alt="orderImage" src={orderImage}/>
                        <MDTypography sx={{color:({palette})=>palette.grey[600], display:{xs:"none", lg:"block"}}}>Order Online Service</MDTypography>
                    </Grid>
                    <Grid item xs={3} textAlign="center">
                        <MDBox component="img" alt="paymentImage" src={paymentImage}/>
                        <MDTypography sx={{color:({palette})=>palette.grey[600], display:{xs:"none", lg:"block"}}}>Easy Payment</MDTypography>
                    </Grid>
                    <Grid item xs={3} textAlign="center">
                        <MDBox component="img" alt="returnImage" src={returnImage}/>
                        <MDTypography sx={{color:({palette})=>palette.grey[600], display:{xs:"none", lg:"block"}}}>Return 72 hours</MDTypography>
                    </Grid>
                </Grid>
            </Container>
        </Card>

        <MDBox pt={6} px={1} mt={6}>
        {pageContent &&  <Footer data={pageContent.Contact}/>}
        </MDBox>
        
    </>
  );
};

export default ShoppinCartView;